
import React, { useEffect, useMemo, useState } from "react";

const QUESTION_TYPES = [
  { value: "singleChoice", label: "Single Choice" },
  { value: "multipleChoice", label: "Multiple Choice" },
  { value: "statementBased", label: "Statement Based" },
  { value: "synonym", label: "Synonym" },
  { value: "antonym", label: "Antonym" },
  { value: "vocabulary", label: "Vocabulary" },
  { value: "fillInTheBlank", label: "Fill in the Blank" },
  { value: "errorDetection", label: "Error Detection" },
  { value: "sentenceImprovement", label: "Sentence Improvement" },
  { value: "phraseReplacement", label: "Phrase Replacement" },
  { value: "clozeTest", label: "Cloze Test" },
  { value: "paraJumble", label: "Para Jumble" },
  { value: "matchFollowing", label: "Match the Following" },
];

const DIFFICULTIES = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const DEFAULT_FORM = {
  subject: "",
  chapter: "",
  group: "",

  questionText: "",
  questionType: "singleChoice",

  options: ["", "", "", ""],
  statements: [],

  highlightedText: "",
  explanation: "",

  correctAnswer: "",

  difficulty: "medium",

  marks: 1,
  negativeMarks: 0,

  imageUrl: "",

  order: 0,
  isActive: true,
};

const QUESTION_TYPES_WITH_OPTIONS = [
  "singleChoice",
  "multipleChoice",
  "statementBased",
  "synonym",
  "antonym",
  "vocabulary",
  "fillInTheBlank",
  "errorDetection",
  "sentenceImprovement",
  "phraseReplacement",
  "clozeTest",
  "paraJumble",
  "matchFollowing",
];

const QUESTION_TYPES_WITH_STATEMENTS = [
  "statementBased",
];

const createInitialState = (initialData = {}) => {
  const data = {
    ...DEFAULT_FORM,
    ...initialData,
  };

  return {
    ...data,

    options:
      Array.isArray(data.options) && data.options.length
        ? data.options
        : ["", "", "", ""],

    statements:
      Array.isArray(data.statements)
        ? data.statements
        : [],

    correctAnswer:
      Array.isArray(data.correctAnswer)
        ? data.correctAnswer
        : data.correctAnswer ?? "",
  };
};

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100";

const textareaClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100";

const labelClass =
  "mb-1.5 block text-sm font-medium text-gray-700";

const sectionClass =
  "rounded-xl border border-gray-200 bg-white p-5 shadow-sm";

const EnglishQuestionForm = ({
  initialData = {},
  subjects = [],
  chapters = [],
  groups = [],

  onSubmit,
  onCancel,

  isLoading = false,

  submitLabel = "Save Question",

  error = null,
}) => {
  const [form, setForm] = useState(() =>
    createInitialState(initialData),
  );

  const [errors, setErrors] = useState({});

  const isEditing = Boolean(initialData?._id);

  useEffect(() => {
    setForm(createInitialState(initialData));
  }, [initialData]);

  const selectedQuestionType = useMemo(
    () =>
      QUESTION_TYPES.find(
        (item) => item.value === form.questionType,
      ),
    [form.questionType],
  );

  const showOptions =
    QUESTION_TYPES_WITH_OPTIONS.includes(
      form.questionType,
    );

  const showStatements =
    QUESTION_TYPES_WITH_STATEMENTS.includes(
      form.questionType,
    );

  const isMultipleChoice =
    form.questionType === "multipleChoice";

  const filteredChapters = useMemo(() => {
    if (!form.subject) return chapters;

    return chapters.filter((chapter) => {
      const chapterSubject =
        chapter.subject?._id || chapter.subject;

      return String(chapterSubject) === String(form.subject);
    });
  }, [chapters, form.subject]);

  const filteredGroups = useMemo(() => {
    if (!form.subject) return groups;

    return groups.filter((group) => {
      const groupSubject =
        group.subject?._id || group.subject;

      return String(groupSubject) === String(form.subject);
    });
  }, [groups, form.subject]);

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleSubjectChange = (value) => {
    setForm((prev) => ({
      ...prev,
      subject: value,
      chapter: "",
      group: "",
    }));

    setErrors((prev) => ({
      ...prev,
      subject: "",
    }));
  };

  const handleQuestionTypeChange = (value) => {
    setForm((prev) => ({
      ...prev,
      questionType: value,

      correctAnswer:
        value === "multipleChoice" ? [] : "",

      options:
        [
          "singleChoice",
          "multipleChoice",
          "statementBased",
          "synonym",
          "antonym",
          "vocabulary",
          "fillInTheBlank",
          "errorDetection",
          "sentenceImprovement",
          "phraseReplacement",
          "clozeTest",
          "paraJumble",
          "matchFollowing",
        ].includes(value)
          ? prev.options?.length
            ? prev.options
            : ["", "", "", ""]
          : [],

      statements:
        value === "statementBased"
          ? prev.statements?.length
            ? prev.statements
            : [""]
          : [],
    }));
  };

  // =====================================================
  // OPTIONS
  // =====================================================

  const addOption = () => {
    setForm((prev) => ({
      ...prev,
      options: [...prev.options, ""],
    }));
  };

  const removeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter(
        (_, optionIndex) => optionIndex !== index,
      ),

      correctAnswer:
        Array.isArray(prev.correctAnswer)
          ? prev.correctAnswer.filter(
              (answer) =>
                answer !== prev.options[index],
            )
          : prev.correctAnswer ===
            prev.options[index]
          ? ""
          : prev.correctAnswer,
    }));
  };

  const updateOption = (index, value) => {
    setForm((prev) => {
      const oldValue = prev.options[index];

      const options = [...prev.options];
      options[index] = value;

      let correctAnswer = prev.correctAnswer;

      if (Array.isArray(correctAnswer)) {
        correctAnswer = correctAnswer.map((answer) =>
          answer === oldValue ? value : answer,
        );
      } else if (correctAnswer === oldValue) {
        correctAnswer = value;
      }

      return {
        ...prev,
        options,
        correctAnswer,
      };
    });
  };

  const toggleCorrectOption = (option) => {
    setForm((prev) => {
      if (isMultipleChoice) {
        const current = Array.isArray(prev.correctAnswer)
          ? prev.correctAnswer
          : [];

        const exists = current.includes(option);

        return {
          ...prev,
          correctAnswer: exists
            ? current.filter(
                (item) => item !== option,
              )
            : [...current, option],
        };
      }

      return {
        ...prev,
        correctAnswer: option,
      };
    });

    setErrors((prev) => ({
      ...prev,
      correctAnswer: "",
    }));
  };

  // =====================================================
  // STATEMENTS
  // =====================================================

  const addStatement = () => {
    setForm((prev) => ({
      ...prev,
      statements: [...prev.statements, ""],
    }));
  };

  const removeStatement = (index) => {
    setForm((prev) => ({
      ...prev,
      statements: prev.statements.filter(
        (_, statementIndex) =>
          statementIndex !== index,
      ),
    }));
  };

  const updateStatement = (index, value) => {
    setForm((prev) => {
      const statements = [...prev.statements];
      statements[index] = value;

      return {
        ...prev,
        statements,
      };
    });
  };

  // =====================================================
  // VALIDATION
  // =====================================================

  const validate = () => {
    const nextErrors = {};

    if (!form.subject) {
      nextErrors.subject =
        "Subject is required.";
    }

    if (!form.questionText.trim()) {
      nextErrors.questionText =
        "Question text is required.";
    }

    if (!form.questionType) {
      nextErrors.questionType =
        "Question type is required.";
    }

    if (
      form.marks === "" ||
      Number(form.marks) < 0
    ) {
      nextErrors.marks =
        "Marks cannot be negative.";
    }

    if (
      form.negativeMarks === "" ||
      Number(form.negativeMarks) < 0
    ) {
      nextErrors.negativeMarks =
        "Negative marks cannot be negative.";
    }

    if (showOptions) {
      const validOptions = form.options.filter(
        (option) => option.trim(),
      );

      if (validOptions.length < 2) {
        nextErrors.options =
          "At least two options are required.";
      }

      if (
        isMultipleChoice &&
        (!Array.isArray(form.correctAnswer) ||
          form.correctAnswer.length === 0)
      ) {
        nextErrors.correctAnswer =
          "Select at least one correct answer.";
      } else if (
        !isMultipleChoice &&
        !String(form.correctAnswer || "").trim()
      ) {
        nextErrors.correctAnswer =
          "Select the correct answer.";
      }
    } else if (
      !Array.isArray(form.correctAnswer) &&
      !String(form.correctAnswer || "").trim()
    ) {
      nextErrors.correctAnswer =
        "Correct answer is required.";
    }

    if (showStatements) {
      const validStatements =
        form.statements.filter(
          (statement) => statement.trim(),
        );

      if (!validStatements.length) {
        nextErrors.statements =
          "At least one statement is required.";
      }
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  // =====================================================
  // SUBMIT
  // =====================================================

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload = {
      ...form,

      subject: form.subject || undefined,

      chapter:
        form.chapter || undefined,

      group:
        form.group || undefined,

      questionText:
        form.questionText.trim(),

      highlightedText:
        form.highlightedText?.trim() || undefined,

      explanation:
        form.explanation?.trim() || undefined,

      imageUrl:
        form.imageUrl?.trim() || undefined,

      options: showOptions
        ? form.options
            .map((option) => option.trim())
            .filter(Boolean)
        : [],

      statements: showStatements
        ? form.statements
            .map((statement) =>
              statement.trim(),
            )
            .filter(Boolean)
        : [],

      correctAnswer:
        Array.isArray(form.correctAnswer)
          ? form.correctAnswer
              .map((answer) =>
                String(answer).trim(),
              )
              .filter(Boolean)
          : String(form.correctAnswer || "").trim(),

      marks: Number(form.marks),

      negativeMarks:
        Number(form.negativeMarks),

      order:
        form.order === ""
          ? 0
          : Number(form.order),

      isActive: Boolean(form.isActive),
    };

    onSubmit?.(payload);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {/* =================================================
          API ERROR
      ================================================= */}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {typeof error === "string"
            ? error
            : error?.data?.message ||
              error?.message ||
              "Something went wrong."}
        </div>
      )}

      {/* =================================================
          BASIC INFORMATION
      ================================================= */}

      <section className={sectionClass}>
        <div className="mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            Question Information
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Configure the subject, question type,
            chapter and question details.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Subject */}
          <div>
            <label className={labelClass}>
              Subject
              <span className="ml-1 text-red-500">
                *
              </span>
            </label>

            <select
              value={form.subject}
              onChange={(e) =>
                handleSubjectChange(
                  e.target.value,
                )
              }
              className={inputClass}
              disabled={isLoading}
            >
              <option value="">
                Select subject
              </option>

              {subjects.map((subject) => (
                <option
                  key={subject._id}
                  value={subject._id}
                >
                  {subject.name}
                </option>
              ))}
            </select>

            {errors.subject && (
              <p className="mt-1 text-xs text-red-600">
                {errors.subject}
              </p>
            )}
          </div>

          {/* Chapter */}
          <div>
            <label className={labelClass}>
              Chapter
            </label>

            <select
              value={form.chapter}
              onChange={(e) =>
                updateField(
                  "chapter",
                  e.target.value,
                )
              }
              className={inputClass}
              disabled={
                isLoading || !form.subject
              }
            >
              <option value="">
                Select chapter
              </option>

              {filteredChapters.map(
                (chapter) => (
                  <option
                    key={chapter._id}
                    value={chapter._id}
                  >
                    {chapter.name}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* Group */}
          <div>
            <label className={labelClass}>
              Question Group
            </label>

            <select
              value={form.group}
              onChange={(e) =>
                updateField(
                  "group",
                  e.target.value,
                )
              }
              className={inputClass}
              disabled={
                isLoading || !form.subject
              }
            >
              <option value="">
                No group
              </option>

              {filteredGroups.map(
                (group) => (
                  <option
                    key={group._id}
                    value={group._id}
                  >
                    {group.name ||
                      group.title ||
                      group.groupType ||
                      "Question Group"}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* Question Type */}
          <div>
            <label className={labelClass}>
              Question Type
              <span className="ml-1 text-red-500">
                *
              </span>
            </label>

            <select
              value={form.questionType}
              onChange={(e) =>
                handleQuestionTypeChange(
                  e.target.value,
                )
              }
              className={inputClass}
              disabled={isLoading}
            >
              {QUESTION_TYPES.map(
                (type) => (
                  <option
                    key={type.value}
                    value={type.value}
                  >
                    {type.label}
                  </option>
                ),
              )}
            </select>

            {errors.questionType && (
              <p className="mt-1 text-xs text-red-600">
                {errors.questionType}
              </p>
            )}
          </div>

          {/* Difficulty */}
          <div>
            <label className={labelClass}>
              Difficulty
            </label>

            <select
              value={form.difficulty}
              onChange={(e) =>
                updateField(
                  "difficulty",
                  e.target.value,
                )
              }
              className={inputClass}
              disabled={isLoading}
            >
              {DIFFICULTIES.map(
                (difficulty) => (
                  <option
                    key={difficulty.value}
                    value={difficulty.value}
                  >
                    {difficulty.label}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* Order */}
          <div>
            <label className={labelClass}>
              Display Order
            </label>

            <input
              type="number"
              min="0"
              value={form.order}
              onChange={(e) =>
                updateField(
                  "order",
                  e.target.value,
                )
              }
              className={inputClass}
              disabled={isLoading}
            />
          </div>
        </div>
      </section>

      {/* =================================================
          QUESTION TEXT
      ================================================= */}

      <section className={sectionClass}>
        <div className="mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            Question Content
          </h2>
        </div>

        <div className="space-y-5">
          <div>
            <label className={labelClass}>
              Question
              <span className="ml-1 text-red-500">
                *
              </span>
            </label>

            <textarea
              rows={5}
              value={form.questionText}
              onChange={(e) =>
                updateField(
                  "questionText",
                  e.target.value,
                )
              }
              placeholder="Enter the English question..."
              className={textareaClass}
              disabled={isLoading}
            />

            {errors.questionText && (
              <p className="mt-1 text-xs text-red-600">
                {errors.questionText}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>
              Highlighted Text
            </label>

            <textarea
              rows={3}
              value={form.highlightedText}
              onChange={(e) =>
                updateField(
                  "highlightedText",
                  e.target.value,
                )
              }
              placeholder="Optional text to highlight..."
              className={textareaClass}
              disabled={isLoading}
            />
          </div>

          <div>
            <label className={labelClass}>
              Image URL
            </label>

            <input
              type="url"
              value={form.imageUrl}
              onChange={(e) =>
                updateField(
                  "imageUrl",
                  e.target.value,
                )
              }
              placeholder="https://example.com/image.jpg"
              className={inputClass}
              disabled={isLoading}
            />
          </div>
        </div>
      </section>

      {/* =================================================
          STATEMENTS
      ================================================= */}

      {showStatements && (
        <section className={sectionClass}>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Statements
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Add the statements used in this
                question.
              </p>
            </div>

            <button
              type="button"
              onClick={addStatement}
              className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
              disabled={isLoading}
            >
              + Add Statement
            </button>
          </div>

          <div className="space-y-3">
            {form.statements.map(
              (statement, index) => (
                <div
                  key={index}
                  className="flex gap-3"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-sm font-semibold text-gray-600">
                    {index + 1}
                  </div>

                  <textarea
                    rows={2}
                    value={statement}
                    onChange={(e) =>
                      updateStatement(
                        index,
                        e.target.value,
                      )
                    }
                    placeholder={`Statement ${index + 1}`}
                    className={textareaClass}
                    disabled={isLoading}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      removeStatement(index)
                    }
                    className="h-10 rounded-lg px-3 text-sm font-medium text-red-600 hover:bg-red-50"
                    disabled={isLoading}
                  >
                    Remove
                  </button>
                </div>
              ),
            )}

            {!form.statements.length && (
              <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
                No statements added.
              </div>
            )}
          </div>

          {errors.statements && (
            <p className="mt-2 text-xs text-red-600">
              {errors.statements}
            </p>
          )}
        </section>
      )}

      {/* =================================================
          OPTIONS
      ================================================= */}

      {showOptions && (
        <section className={sectionClass}>
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Answer Options
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                {isMultipleChoice
                  ? "Select all correct answers."
                  : "Select the correct answer."}
              </p>
            </div>

            <button
              type="button"
              onClick={addOption}
              className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              disabled={isLoading}
            >
              + Add Option
            </button>
          </div>

          <div className="space-y-3">
            {form.options.map(
              (option, index) => {
                const isCorrect =
                  Array.isArray(
                    form.correctAnswer,
                  )
                    ? form.correctAnswer.includes(
                        option,
                      )
                    : form.correctAnswer ===
                      option &&
                      option !== "";

                return (
                  <div
                    key={index}
                    className={`rounded-lg border p-3 transition ${
                      isCorrect
                        ? "border-green-300 bg-green-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          toggleCorrectOption(
                            option,
                          )
                        }
                        disabled={
                          isLoading ||
                          !option.trim()
                        }
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition ${
                          isCorrect
                            ? "border-green-600 bg-green-600 text-white"
                            : "border-gray-300 bg-white text-gray-500 hover:border-blue-400"
                        }`}
                        title={
                          isCorrect
                            ? "Correct answer"
                            : "Mark as correct"
                        }
                      >
                        {String.fromCharCode(
                          65 + index,
                        )}
                      </button>

                      <input
                        type="text"
                        value={option}
                        onChange={(e) =>
                          updateOption(
                            index,
                            e.target.value,
                          )
                        }
                        placeholder={`Option ${String.fromCharCode(
                          65 + index,
                        )}`}
                        className={inputClass}
                        disabled={isLoading}
                      />

                      {form.options.length >
                        2 && (
                        <button
                          type="button"
                          onClick={() =>
                            removeOption(
                              index,
                            )
                          }
                          className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {isCorrect && (
                      <div className="mt-2 ml-12 text-xs font-medium text-green-700">
                        Correct answer
                      </div>
                    )}
                  </div>
                );
              },
            )}
          </div>

          {errors.options && (
            <p className="mt-2 text-xs text-red-600">
              {errors.options}
            </p>
          )}

          {errors.correctAnswer && (
            <p className="mt-2 text-xs text-red-600">
              {errors.correctAnswer}
            </p>
          )}
        </section>
      )}

      {/* =================================================
          CORRECT ANSWER FOR NON-OPTION QUESTIONS
      ================================================= */}

      {!showOptions && (
        <section className={sectionClass}>
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900">
              Correct Answer
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Enter the expected answer.
            </p>
          </div>

          <textarea
            rows={3}
            value={
              Array.isArray(
                form.correctAnswer,
              )
                ? form.correctAnswer.join(", ")
                : form.correctAnswer
            }
            onChange={(e) =>
              updateField(
                "correctAnswer",
                e.target.value,
              )
            }
            placeholder="Enter the correct answer..."
            className={textareaClass}
            disabled={isLoading}
          />

          {errors.correctAnswer && (
            <p className="mt-2 text-xs text-red-600">
              {errors.correctAnswer}
            </p>
          )}
        </section>
      )}

      {/* =================================================
          MARKING
      ================================================= */}

      <section className={sectionClass}>
        <div className="mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            Marking
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <label className={labelClass}>
              Marks
            </label>

            <input
              type="number"
              min="0"
              step="0.25"
              value={form.marks}
              onChange={(e) =>
                updateField(
                  "marks",
                  e.target.value,
                )
              }
              className={inputClass}
              disabled={isLoading}
            />

            {errors.marks && (
              <p className="mt-1 text-xs text-red-600">
                {errors.marks}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>
              Negative Marks
            </label>

            <input
              type="number"
              min="0"
              step="0.25"
              value={form.negativeMarks}
              onChange={(e) =>
                updateField(
                  "negativeMarks",
                  e.target.value,
                )
              }
              className={inputClass}
              disabled={isLoading}
            />

            {errors.negativeMarks && (
              <p className="mt-1 text-xs text-red-600">
                {errors.negativeMarks}
              </p>
            )}
          </div>

          <div className="flex items-end">
            <label className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                checked={Boolean(
                  form.isActive,
                )}
                onChange={(e) =>
                  updateField(
                    "isActive",
                    e.target.checked,
                  )
                }
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={isLoading}
              />

              <span>
                <span className="block text-sm font-medium text-gray-800">
                  Active Question
                </span>

                <span className="block text-xs text-gray-500">
                  Make this question available
                  for quiz selection.
                </span>
              </span>
            </label>
          </div>
        </div>
      </section>

      {/* =================================================
          EXPLANATION
      ================================================= */}

      <section className={sectionClass}>
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900">
            Explanation
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Optional explanation shown after the
            quiz is completed.
          </p>
        </div>

        <textarea
          rows={5}
          value={form.explanation}
          onChange={(e) =>
            updateField(
              "explanation",
              e.target.value,
            )
          }
          placeholder="Enter the explanation..."
          className={textareaClass}
          disabled={isLoading}
        />
      </section>

      {/* =================================================
          FORM ACTIONS
      ================================================= */}

      <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading
            ? "Saving..."
            : isEditing
            ? "Update Question"
            : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default EnglishQuestionForm;

